"use client";

import { cn } from "@/lib/utils";
import Link from "next/link";
import * as React from "react";

/**
 * Breadcrumb primitive — compound pattern aligned with shadcn/ui conventions.
 *
 * Usage:
 *   <Breadcrumb>
 *     <BreadcrumbList>
 *       <BreadcrumbItem>
 *         <BreadcrumbLink href="/integrations">Integrations</BreadcrumbLink>
 *       </BreadcrumbItem>
 *       <BreadcrumbSeparator />
 *       <BreadcrumbItem>
 *         <BreadcrumbPage>Slack</BreadcrumbPage>
 *       </BreadcrumbItem>
 *     </BreadcrumbList>
 *   </Breadcrumb>
 */

const Breadcrumb = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<"nav">>(function Breadcrumb(
  { className, ...props },
  ref,
) {
  return <nav ref={ref} aria-label="Breadcrumb" className={cn("", className)} {...props} />;
});

const BreadcrumbList = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<"ol">>(function BreadcrumbList(
  { className, ...props },
  ref,
) {
  return (
    <ol
      ref={ref}
      className={cn("flex flex-wrap items-center gap-1.5 text-sm leading-5 sm:gap-1.5", className)}
      {...props}
    />
  );
});

const BreadcrumbItem = React.forwardRef<HTMLLIElement, React.ComponentPropsWithoutRef<"li">>(function BreadcrumbItem(
  { className, ...props },
  ref,
) {
  return <li ref={ref} className={cn("inline-flex items-center gap-1.5", className)} {...props} />;
});

interface BreadcrumbLinkProps extends Omit<React.ComponentPropsWithoutRef<typeof Link>, "href"> {
  href?: string;
  asChild?: boolean;
}

const BreadcrumbLink = React.forwardRef<HTMLAnchorElement, BreadcrumbLinkProps>(function BreadcrumbLink(
  { className, href, ...props },
  ref,
) {
  const base = "text-ds-text-slate hover:underline transition-colors";
  if (!href) {
    return <span className={cn(base, className)} {...(props as React.HTMLAttributes<HTMLSpanElement>)} />;
  }
  return <Link ref={ref} href={href} className={cn(base, className)} {...props} />;
});

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, React.ComponentPropsWithoutRef<"span">>(
  function BreadcrumbPage({ className, ...props }, ref) {
    return (
      <span ref={ref} aria-current="page" className={cn("text-ds-text-primary font-medium", className)} {...props} />
    );
  },
);

function BreadcrumbSeparator({ className, children, ...props }: React.ComponentPropsWithoutRef<"span">) {
  return (
    <span aria-hidden="true" className={cn("text-ds-text-slate select-none", className)} {...props}>
      {children ?? "›"}
    </span>
  );
}

export { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator };
